/**
 * Mid-century prints — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wall_art`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const midCenturyPrintsPack: ToilePack = {
    slug: 'mid-century-prints',
    category: 'wall-art',
    name: 'Mid-century prints',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'starburst', label: 'Starburst', subject: 'an atomic starburst of thin radiating spokes tipped with small round beads, a boomerang shape crossing behind it'},
        {key: 'house', label: 'Desert house', subject: 'a low desert house with a butterfly roof, a breeze-block wall, one tall palm and a kidney-shaped pool'},
        {key: 'cat', label: 'Long cat', subject: 'a stylized cat with a long thin neck and almond eyes, sitting beside a cocktail glass holding one olive'},
    ],
    styles: [
        {key: 'screen', label: 'Screen print', style: 'a textured screen print with visible grain, slightly off-register color blocks and hand-cut edges', palette: 'Mustard yellow, teal, burnt orange and walnut brown on cream paper', framing: 'Centered on the sheet with a wide even margin, the motif small and confident'},
        {key: 'geometric', label: 'Geometric', style: 'hard-edged flat geometry, bold clean shapes with no outlines and smooth gradient-free fills', palette: 'A four-color palette of olive, orange, charcoal and off-white', framing: 'Full-bleed across the whole sheet with the shapes cropped by the edges'},
        {key: 'linear', label: 'Linear', style: 'thin uniform ink linework drawn over flat color panels', palette: 'Two-color ink, a charcoal line over one flat block of terracotta on ivory', framing: 'Centered inside a thin ruled border with a generous margin'},
    ],
    directions: ['for a home bar', 'in pink and orange', 'with a record player instead'],
    seo: {
        title: 'Mid century modern wall art: starburst, house, cat',
        metaDescription: 'Nine mid-century prints — a starburst, a desert house and a long cat — in screen print, geometric or linear style, mustard, teal and walnut palette.',
        h1: 'Mid century prints: starburst, desert house and long cat',
        tagline: 'Nine prints, three 1958 subjects, three treatments each — mustard, teal, orange and walnut brown.',
        intro: 'The 1958 register: an atomic starburst, a Palm Springs house with a butterfly roof, a stylized cat with a long neck. These nine cross those three subjects with a textured screen-print finish, a bold geometric treatment and a clean linear style — mustard, teal, orange and walnut brown, the print for the Eames-chair corner.',
        useCases: [
            'the print for an Eames-chair corner',
            'a home bar or den wall',
            'a retro-styled kitchen',
            'a gift for a mid-century design fan',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each print is drawn at A3 proportions with an even margin, so it prints clean at A3 or scales to A4.'},
            {q: 'Can I get a different palette, like pink and orange?', a: 'Yes — the direction field takes that request and redraws it in the same three treatments.'},
            {q: 'Is there any text on the print?', a: 'No — these are flat 2D artwork with no lettering, frame or mockup.'},
        ],
    },
}
